import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLinkIcon } from '@radix-ui/react-icons';

interface InvoiceItem {
  name: string;
  price: number;
}

interface InvoiceData {
  id: string;
  invoice_number: string;
  date: string;
  supplier_name: string;
  net: number;
  vat_percentage?: number;
  vat_amount: number;
  gross: number;
  items: InvoiceItem[];
  pdf_url?: string;
}

interface InvoiceCardProps {
  invoice: InvoiceData;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ 
  invoice, 
  expanded = false, 
  onToggleExpand 
}) => {
  const navigate = useNavigate();

  // Format date
  const formatDate = (dateStr: string) => {
    return dateStr; // You can add proper date formatting if needed
  };

  const viewInvoiceDetails = () => {
    navigate(`/invoice/${invoice.id}`);
  };

  return (
    <div className="bg-white dark:bg-dark-sidebar rounded-lg shadow overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Invoice #{invoice.invoice_number}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(invoice.date)}</p>
          </div>
          <button
            onClick={viewInvoiceDetails}
            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary hover:bg-blue-50 dark:hover:bg-blue-900/30"
          >
            <ExternalLinkIcon className="w-3 h-3 mr-1" />
            View
          </button>
        </div>
        
        {/* Supplier */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Supplier</p>
          <p className="text-sm text-gray-900 dark:text-gray-200">{invoice.supplier_name}</p>
        </div>
        
        {/* Amounts */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Net</p>
            <p className="text-sm font-medium dark:text-gray-200">{invoice.net.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              VAT {invoice.vat_percentage ? `(${invoice.vat_percentage}%)` : ''}
            </p>
            <p className="text-sm font-medium dark:text-gray-200">{invoice.vat_amount.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Total</p>
            <p className="text-sm font-semibold text-primary">{invoice.gross.toFixed(2)}</p>
          </div>
        </div>
        
        {/* Expandable Items Section */}
        {expanded && invoice.items.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Items</p>
            <div className="max-h-40 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Item
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {invoice.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 whitespace-normal text-xs text-gray-900 dark:text-gray-200">
                        {item.name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 text-right">
                        { (item.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Expand/Collapse Button */}
        {invoice.items.length > 0 && (
          <button
            onClick={onToggleExpand}
            className="mt-3 w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center justify-center"
          >
            {expanded ? 'Show Less' : 'Show Items'}
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceCard;
